import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React from 'react';
import breakpoints from '../../../config/breakpoints';
import mediaURL from '../../../util/mediaURL';

export function buildUrl(
  url,
  imageMode = 'Crop',
  width = 0,
  height = 0,
  quality = 75,
  focalPointLeft = 0.5,
  focalPointTop = 0.5,
  modifiedDate = ''
) {
  let str = mediaURL(url);
  str += `?rxy=${focalPointLeft},${focalPointTop}&rmode=${imageMode}`;

  if (width > 0) str += `&width=${width}`;
  if (height > 0) str += `&height=${height}`;

  str += `&quality=${quality}`;

  // rnd needed explicitly to break cache.
  str += `&rnd=${modifiedDate}`;

  return str;
}

const propTypes = exact({
  sizes: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ).isRequired,
  url: PropTypes.string,
  loading: PropTypes.string,
  className: PropTypes.string,
  alt: PropTypes.string,
  focalPointLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  focalPointTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  modifiedDate: PropTypes.string,
});

const defaultProps = {
  url: '',
  loading: 'lazy',
  className: undefined,
  alt: undefined,
  focalPointLeft: 0.5,
  focalPointTop: 0.5,
  modifiedDate: '',
};

// extra breakpoint used for super large screens
const UPPER_BREAKPOINT_SIZE = 1920;
// default is an image that spans the width of the breakpoint with aspect ratio of 16:9
const FALLBACK_IMAGE_DETAIL = [100, 16 / 9];

function fulfillBreakpointImageDetails(breakpointConfigValues, sizes = [], url = '') {
  if (breakpointConfigValues.length === sizes.length) return sizes;

  if (breakpointConfigValues.length < sizes.length) {
    // eslint-disable-next-line no-console
    console.warn('Picture warning: you have defined more size items than breakpoints', url);
    return sizes.slice(0, breakpointConfigValues.length);
  }

  if (sizes.length === 0) {
    return new Array(breakpointConfigValues.length).fill(FALLBACK_IMAGE_DETAIL);
  }

  for (let i = 0; i < breakpointConfigValues.length; i += 1) {
    if (sizes.length - 1 < i) {
      sizes.push(sizes[sizes.length - 1]);
    }
  }

  return sizes;
}

function Picture({
  sizes,
  url,
  loading,
  className,
  alt,
  focalPointLeft,
  focalPointTop,
  modifiedDate,
}) {
  // In this project our last breakpoint is 1440, this is not that large on super large screens
  // such as the iMac that is 2605. So we will introduce a larger upper size to replicate
  // the last breakpoint at an upper size.

  const breakpointConfigValues = Object.values(breakpoints).concat([UPPER_BREAKPOINT_SIZE]);
  // The second value concerns height when used as a string this again become an absolute pixel
  // size. The alternative is to use a decimal to declare a ratio of width to height
  // (16: 9 would be written as 1.77)
  // or omit it so the aspect ratio is preserved.
  const breakpointImageDetails = fulfillBreakpointImageDetails(breakpointConfigValues, sizes, url);

  const sourceContent = [];
  let imageContent;

  for (let i = 0; i < breakpointConfigValues.length; i += 1) {
    const breakpointValue = breakpointConfigValues[i];

    // We need the next breakpoint up in order to allow our image to be sized up to that point
    const nextBreakpointValue =
      i < breakpointConfigValues.length - 1 ? breakpointConfigValues[i + 1] : breakpointValue;

    const breakpointImageDetail = breakpointImageDetails[i];
    const width = breakpointImageDetail[0];
    const height = breakpointImageDetail.length > 1 ? breakpointImageDetail[1] : '';

    // Check format of values passed, should be either string or int / float
    const isWidthAnInteger = typeof width === 'number';
    const isHeightAnRatio = typeof height === 'number';

    let widthOfImageInPixels;
    let heightOfImageInPixels;
    let imageLayoutWidth;

    if (isWidthAnInteger) {
      // Percentage of largest possible width used, used to call actual image size
      widthOfImageInPixels = Math.round((width / 100) * nextBreakpointValue);

      // informs the browser of the rough size that the image will be rendered at
      imageLayoutWidth = `${width}vw`;
    } else {
      // actual width declared for image
      widthOfImageInPixels = parseInt(width, 10);
      imageLayoutWidth = `${widthOfImageInPixels}px`;
    }

    if (isHeightAnRatio) {
      // set actual image height based on ratio of width
      heightOfImageInPixels = Math.ceil(widthOfImageInPixels / height);
    } else {
      // use actual parsed pixel height
      heightOfImageInPixels = parseInt(height, 10);
    }

    // Mobile we try to compress a little more
    const quality = i <= 2 ? 65 : 80;
    const urlResolutionX1 = `${buildUrl(
      url,
      'Crop',
      widthOfImageInPixels,
      heightOfImageInPixels,
      quality,
      focalPointLeft,
      focalPointTop,
      modifiedDate
    )} ${widthOfImageInPixels}w`;
    const urlResolutionX2 = `${buildUrl(
      url,
      'Crop',
      widthOfImageInPixels * 2,
      heightOfImageInPixels * 2,
      quality,
      focalPointLeft,
      focalPointTop,
      modifiedDate
    )} ${widthOfImageInPixels * 2}w`;

    // Mobile only provide x1 resolution for SEO, other provide the two pixel densities
    const srcSetString = i <= 1 ? `${urlResolutionX1}` : `${urlResolutionX1}, ${urlResolutionX2}`;
    // Create the source node for this breakpoint
    sourceContent.push(
      <source
        key={`${srcSetString}-${breakpointValue}`}
        srcSet={srcSetString}
        media={`(min-width: ${breakpointValue}px)`}
        sizes={imageLayoutWidth}
      />
    );
    if (i === breakpointConfigValues.length - 1) {
      // pluck out we a standard desktop size to use for the default image
      const imageSrcSetString = buildUrl(
        url,
        'Crop',
        widthOfImageInPixels,
        heightOfImageInPixels,
        quality,
        focalPointLeft,
        focalPointTop,
        modifiedDate
      );
      imageContent = (
        <img
          key={`${imageSrcSetString}-image`}
          loading={loading}
          className={className}
          alt={alt}
          src={imageSrcSetString}
        />
      );
    }
  }

  sourceContent.reverse();

  return (
    <picture>
      {sourceContent}
      {imageContent}
    </picture>
  );
}

Picture.propTypes = propTypes;
Picture.defaultProps = defaultProps;

export default Picture;
